import { IVec3, Vec3 } from "@base/math/Vec3";
import { TriangleHelper } from "@biz/items/sunSail/logic/TriangleHelper";

export type SunSailGroup = SunSailGroup3 | SunSailGroup4;

class SunSailGroupBase3 {
  public points: IVec3[] = [];
  public poleIndices: number[] = [];

  public get vab(): IVec3 {
    return Vec3.sub(this.points[1], this.points[0]);
  }

  public get vac(): IVec3 {
    return Vec3.sub(this.points[2], this.points[0]);
  }

  public get vbc(): IVec3 {
    return Vec3.sub(this.points[2], this.points[1]);
  }

  public get ab(): number {
    return Vec3.len(this.vab);
  }

  public get ac(): number {
    return Vec3.len(this.vac);
  }

  public get bc(): number {
    return Vec3.len(this.vbc);
  }

  public calcAngleForIndex(index: number): number {
    const lastIndex = (index + this.points.length - 1) % this.points.length;
    const nextIndex = (index + 1) % this.points.length;
    return Vec3.calcAngle(this.points[lastIndex], this.points[index], this.points[nextIndex]);
  }

  public calcNetSideLength(a: number, b: number): number {
    return Vec3.len(this.points[a], this.points[b]);
  }
}

export class SunSailGroup3 extends SunSailGroupBase3 {
  public pointCount = 3 as const;

  public constructor(poleIndices: number[]) {
    super();
    this.points = [Vec3.empty, Vec3.empty, Vec3.empty];
    this.poleIndices = poleIndices;
  }

  public get α(): number {
    return Math.acos((this.ac * this.ac + this.ab * this.ab - this.bc * this.bc) / (2 * this.ab * this.ac));
  }

  public get β(): number {
    return Math.acos((this.bc * this.bc + this.ab * this.ab - this.ac * this.ac) / (2 * this.ab * this.bc));
  }

  public get γ(): number {
    return Math.acos((this.bc * this.bc + this.ac * this.ac - this.ab * this.ab) / (2 * this.ac * this.bc));
  }

  public calcArea(): number {
    return TriangleHelper.calcArea(this.ab, this.bc, this.ac);
  }
}

export class SunSailGroup4 extends SunSailGroupBase3 {
  public pointCount = 4 as const;

  public constructor(poleIndices: number[]) {
    super();
    this.points = [Vec3.empty, Vec3.empty, Vec3.empty, Vec3.empty];
    this.poleIndices = poleIndices;
  }

  public get vcd(): IVec3 {
    return Vec3.sub(this.points[2], this.points[3]);
  }

  public get vbd(): IVec3 {
    return Vec3.sub(this.points[1], this.points[3]);
  }

  public get vad(): IVec3 {
    return Vec3.sub(this.points[0], this.points[3]);
  }

  public get cd(): number {
    return Vec3.len(this.vcd);
  }

  public get bd(): number {
    return Vec3.len(this.vbd);
  }

  public get ad(): number {
    return Vec3.len(this.vad);
  }

  public calcArea(): number {
    let area = 0;
    area += TriangleHelper.calcArea(this.ab, this.bc, this.ac);
    area += TriangleHelper.calcArea(this.ac, this.cd, this.ad);
    return area;
  }
}
